import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "@/api/client";

const AuthContext = createContext(null);

/**
 * AuthProvider state model:
 *   user === undefined → still checking session (initial mount)
 *   user === null      → confirmed unauthenticated
 *   user === object    → authenticated; .profile, .role, .access_level, etc.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);
  // V1.1f-hotfix — exposed alias so call-sites can use the intent-revealing
  // name `refreshMe()`. Internally still the same `/auth/me` round-trip.
  const refreshMe = refresh;

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const body = { ...payload };
    if (body.referral_code) body.referral_code = String(body.referral_code).trim().toUpperCase();
    const { data } = await api.post("/auth/register", body);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
    }
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const { data } = await api.patch("/profile", patch);
    setUser(data);
    return data;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading: user === undefined,
      authenticated: !!user,
      isAdmin: user?.role === "admin",
      login,
      register,
      logout,
      refresh,
      refreshMe,
      updateProfile,
    }),
    [user, login, register, logout, refresh, refreshMe, updateProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
